import CurrencyIcon from "components/shared/CurrencyIcon";
import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import RadioFormComponent from "components/shared/FormComponent/RadioFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import StyledButton from "components/shared/StyledButton";
import TitleSeparation from "components/shared/TitleSeparation";
import CreateChannelModal from "containers/Channels/CreateChannelModal";
import type { ChannelParams, GroupParams } from "containers/TermFormContainer";
import ChannelsStep from "containers/TermFormContainer/ChannelsStep";
import { formatChannelsForSaving, getGroupWithChannels } from "containers/TermFormContainer/utils";
import { PortalType } from "contexts/portal.context";
import { formatDateToIso, getCleanStartorEndDay } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import type { UseFormClearErrors, UseFormSetError, UseFormSetValue } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TermTemplate } from "store/termTemplatesStore";
import { createTermTemplate, updateTermTemplate } from "store/termTemplatesStore";
import { fetchTitleFormatTypes } from "store/titleFormatTypeStore";

import PressTermTemplateForm from "../PressTermTemplateForm";
import { getTemplateChannels } from "../utils";
import styles from "./style.module.scss";

interface TermTemplateForm {
    mode: "new" | "edit";
    template?: TermTemplate;
    onClose: () => void;
    /**
     * When set, the created template becomes a framework contract for this beneficiary.
     */
    beneficiaryId?: number;
}

interface ITermTemplateData {
    name: string;
    channels: any;
    compensate: number;
    advancePayment: number | string;
    startDate: Date | null;
    endDate: Date | null;
}

export type TTermTemplateForm = {
    setError: UseFormSetError<ITermTemplateData>;
    clearErrors: UseFormClearErrors<ITermTemplateData>;
    setValue: UseFormSetValue<ITermTemplateData>;
};

const TermTemplateForm = ({ mode, template, onClose, beneficiaryId }: TermTemplateForm) => {
    const portalType = useContext(PortalType);
    if (portalType === "press" || portalType === "audiovisual") {
        return (
            <PressTermTemplateForm mode={mode} template={template} onClose={onClose} beneficiaryId={beneficiaryId} />
        );
    }
    return <BookTermTemplateForm mode={mode} template={template} onClose={onClose} beneficiaryId={beneficiaryId} />;
};

const BookTermTemplateForm = ({ mode, template, onClose, beneficiaryId }: TermTemplateForm) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { channels, channelCompensationGroups } = useAppSelector((state) => state.channels);
    const { items: titleFormatTypes, status: titleFormatTypesStatus } = useAppSelector(
        (state) => state.titleFormatTypes,
    );
    const { getCompanySettings, companyInfos } = useCompanySettings();
    const usesChannelCompensation = !!getCompanySettings("uses_interchannel_compensation");
    const formatTypesForTemplate = useMemo(
        () => titleFormatTypes.map((formatType) => ({ id: formatType.id, name: formatType.name })),
        [titleFormatTypes],
    );

    useEffect(() => {
        if (titleFormatTypesStatus !== "idle") return;
        dispatch(fetchTitleFormatTypes());
    }, [dispatch, titleFormatTypesStatus]);

    const nameInputRef = useRef(null);

    /**
     * Channels chosen on template and their data (for edit).
     */
    const templateChannels = getTemplateChannels(template?.configuration, channels, {
        usesCompensation: usesChannelCompensation,
    });

    const [activatedChannels, setActivatedChannels] = useState<number[]>([]);
    const [chosenChannels, setChosenChannels] = useState<ChannelParams[]>(
        mode === "edit" ? templateChannels ?? [] : [],
    );
    const [addChannel, setAddChannel] = useState(false);

    /**
     * Groups chosen on the template and their data (for edit).
     */
    const templateGroups = getGroupWithChannels(channelCompensationGroups, templateChannels);
    const templateGroupsIDs = templateGroups.map((template) => template.id);
    const [chosenGroups, setChosenGroups] = useState<GroupParams[]>(mode === "edit" ? templateGroups : []);
    const [activeGroups, setActiveGroups] = useState<number[]>(mode === "edit" ? templateGroupsIDs : []);
    const [isLoading, setIsLoading] = useState(false);

    const compensationOptions = [
        { label: t("ConnectTitle.No") },
        { label: t("ConnectTitle.Yes"), defaultChecked: true },
    ];

    // Array of channels that aren't within a group:
    const filteredChannels = useMemo(() => {
        const groupIds = channelCompensationGroups.map((group) => group.id);
        return usesChannelCompensation
            ? channels.filter((channel) => !groupIds.includes(channel?.compensationGroupId ?? -2))
            : channels;
    }, [channels, channelCompensationGroups, usesChannelCompensation]);

    // Array of groups with their channels:
    const groupsWithChannels = useMemo(() => {
        const allGroups = channelCompensationGroups.map((group) => {
            const connectedChannels = channels.filter((channel) => channel.compensationGroupId === group.id);
            return mode === "new"
                ? { ...group, channels: connectedChannels ?? [], synced: true }
                : { ...group, channels: connectedChannels };
        });

        return allGroups.filter((group) => (group.channels && group.channels.length > 0) ?? false);
    }, [channelCompensationGroups, channels]);

    const companyCompensateValue = +(companyInfos?.useCompensationByDefault ?? 1);

    const defaultValues: ITermTemplateData = {
        name: mode === "edit" ? template?.name ?? "" : "",
        channels: [],
        compensate:
            mode === "edit" ? template?.configuration.compensate ?? companyCompensateValue : companyCompensateValue,
        advancePayment:
            mode === "edit"
                ? (template?.configuration.advancePayment ? +template?.configuration.advancePayment : 0) / 100
                : "",
        startDate:
            mode === "edit" && template?.configuration.startDate
                ? getCleanStartorEndDay(template.configuration.startDate)
                : null,
        endDate:
            mode === "edit" && template?.configuration.endDate
                ? getCleanStartorEndDay(template.configuration.endDate)
                : null,
    };

    const {
        control,
        handleSubmit,
        formState: { errors },
        clearErrors,
        setError,
        setValue,
    } = useForm<ITermTemplateData>({
        defaultValues,
        mode: "all",
    });

    const handleErrorScroll = () => {
        if (errors) {
            nameInputRef.current &&
                (nameInputRef.current as any).scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            return;
        }
    };

    const onSubmit = async (form: any) => {
        setIsLoading(true);
        const channelsData = formatChannelsForSaving(chosenChannels, channels, {
            keepShowDefaultConfiguration: true,
        });
        const configuration = {
            channels: channelsData,
            compensate: form.compensate,
            advancePayment: Math.round((+form.advancePayment.toString().replace(",", ".") || 0) * 100),
            startDate: form.startDate instanceof Date ? formatDateToIso(form.startDate) : null,
            endDate: form.endDate instanceof Date ? formatDateToIso(form.endDate) : null,
        };

        if (companyInfos) {
            const termData = {
                name: form.name ?? "",
                companyId: companyInfos?.id,
                configuration: configuration,
                ...(beneficiaryId ? { beneficiaryId } : {}),
            };

            if (mode === "new") {
                const response = await dispatch(createTermTemplate({ data: termData }));
                if (response.meta.requestStatus === "fulfilled") {
                    onClose();
                }
            } else {
                if (template) {
                    const response = await dispatch(updateTermTemplate({ templateId: template?.id, data: termData }));
                    if (response.meta.requestStatus === "fulfilled") {
                        onClose();
                    }
                }
            }
        }
        setIsLoading(false);
    };

    const isFrameworkContract = beneficiaryId !== undefined;
    const sectionTitleStyle = { fontSize: "0.875rem" };

    return (
        <form className={styles["term-form-container"]} onSubmit={handleSubmit(onSubmit)}>
            <TextFormComponent
                label={isFrameworkContract ? t("TermTemplates.FrameworkContractName") : t("TermTemplates.TemplateName")}
                control={control}
                controlName="name"
                sx={{ marginBottom: "var(--section-gap)" }}
                required
                error={errors.name}
                ref={nameInputRef}
            />
            {isFrameworkContract && (
                <>
                    <TitleSeparation title={t("TermTemplates.ValidityPeriod")} titleStyle={sectionTitleStyle} />
                    <div
                        style={{ display: "flex", gap: "1rem", marginTop: "1rem", marginBottom: "var(--section-gap)" }}
                    >
                        <div style={{ flex: 1 }}>
                            <DateFormComponent
                                label={t("TermTemplates.StartDate")}
                                control={control}
                                controlName="startDate"
                                error={errors.startDate}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <DateFormComponent
                                label={t("TermTemplates.EndDate")}
                                control={control}
                                controlName="endDate"
                                error={errors.endDate}
                            />
                        </div>
                    </div>
                </>
            )}
            <TitleSeparation title={t("TermTemplates.AdvanceSettings")} titleStyle={sectionTitleStyle} />
            <div className={styles["advance-settings"]}>
                <div className={styles["advance-container"]}>
                    <NumberFormComponent
                        label={t("ConnectTitle.Deposit")}
                        control={control}
                        controlName="advancePayment"
                        placeholder="0"
                        icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                        error={errors.advancePayment}
                        acceptsNegative
                        float
                        helpdeskInfo={{
                            text: t("Documentation.UnderstandBalance"),
                            link: "https://helpdesk.crealo.app/fr/article/comment-remplir-le-champ-solde-du-contrat-135p0sx/?bust=1712759820770",
                        }}
                    />
                    <span className={styles["info"]}>{t("ConnectTitle.AdvanceInfo")}</span>
                </div>
                <div className="advance-compensation-radio">
                    <RadioFormComponent
                        id="term-compensate"
                        label={t("ConnectTitle.Compensate")}
                        control={control}
                        controlName="compensate"
                        error={errors.compensate}
                        options={compensationOptions}
                    />
                </div>
            </div>

            <TitleSeparation title={t("TermTemplates.ChannelsPercentages")} titleStyle={sectionTitleStyle} />
            <ChannelsStep
                from={"templates"}
                channels={channels}
                chosenChannels={chosenChannels}
                setChosenChannels={setChosenChannels}
                activatedChannels={activatedChannels}
                setActivatedChannels={setActivatedChannels}
                mode={mode}
                usesCompensation={usesChannelCompensation}
                groupsWithChannels={groupsWithChannels}
                activeGroups={activeGroups}
                setActiveGroups={setActiveGroups}
                chosenGroups={chosenGroups}
                setChosenGroups={setChosenGroups}
                filteredChannels={filteredChannels}
                onAddChannel={setAddChannel}
                useFormData={{ setError, setValue, clearErrors }}
                formatTypesOnWork={formatTypesForTemplate}
            />

            <StyledButton
                buttonType="primary"
                fullWidth
                type="submit"
                onClick={handleErrorScroll}
                loading={isLoading}
                stickyShadow
            >
                {mode === "new"
                    ? isFrameworkContract
                        ? t("TermTemplates.CreateFrameworkContract")
                        : t("TermTemplates.CreateTemplate")
                    : t("TermTemplates.ConfirmChanges")}
            </StyledButton>

            <CreateChannelModal mode="new" openModal={addChannel} closeModal={() => setAddChannel(false)} />
        </form>
    );
};

export default TermTemplateForm;
